import React, { useState, useRef, useEffect, useId } from 'react';
import { deleteClient, archiveClient, unarchiveClient, getArchivedClients, updateClient } from './api';
import ClientHistory from './ClientHistory';
import AddressAutocomplete from './AddressAutocomplete';
import { formatPhone } from '../format';
import RecurringModal from './RecurringModal';
import { useTranslation } from '../i18n';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const editModalCSS = `
  .ms-client-save:hover:not(:disabled) { background: #0f4d2c !important; }
`;

function scheduleLabel(client) {
  if (client.visit_type === 'one_time') return '1x visit';
  if (!client.recurring_enabled) return null;
  const freq = client.recurring_frequency;
  const day = client.recurring_day_of_week !== null && client.recurring_day_of_week !== undefined
    ? DAYS[client.recurring_day_of_week] : null;
  if (freq === 'weekly') return day ? `Weekly · ${day}` : 'Weekly';
  if (freq === 'biweekly') return day ? `Every 2 weeks · ${day}` : 'Every 2 weeks';
  if (freq === 'custom_weeks') return `Every ${client.recurring_interval}w${day ? ' · ' + day : ''}`;
  if (freq === 'monthly') return client.recurring_day_of_month ? `Monthly · day ${client.recurring_day_of_month}` : 'Monthly';
  return null;
}

function ClientMenu({ client, isArchived, onHistory, onSchedule, onEdit, onArchive, onUnarchive, onDelete, onClose, t }) {
  const ref = useRef(null);

  useEffect(() => {
    const handleOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('touchstart', handleOutside);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('touchstart', handleOutside);
      document.removeEventListener('keydown', handleKey);
    };
  }, [onClose]);

  const items = isArchived ? [
    { label: t('client_list.visit_history'), action: onHistory, color: '#1a1a1a' },
    { label: t('client_list.restore_client'), action: onUnarchive, color: '#14653a' },
    { label: t('client_list.delete_client'), action: onDelete, color: '#dc2626' },
  ] : [
    { label: t('client_list.edit_client'), action: onEdit, color: '#1a1a1a' },
    { label: t('client_list.visit_history'), action: onHistory, color: '#1a1a1a' },
    { label: t('client_list.schedule'), action: onSchedule, color: '#1a1a1a' },
    { label: t('client_list.archive_client'), action: onArchive, color: '#5f6b58' },
    { label: t('client_list.delete_client'), action: onDelete, color: '#dc2626' },
  ];

  return (
    <div ref={ref} style={menuStyles.dropdown} role="menu">
      {items.map((item, i) => (
        <button
          key={i}
          role="menuitem"
          style={{
            ...menuStyles.item,
            color: item.color,
            borderBottom: i < items.length - 1 ? '1px solid #f5f5f5' : 'none'
          }}
          onMouseDown={e => { e.stopPropagation(); item.action(); }}
          onTouchEnd={e => { e.preventDefault(); e.stopPropagation(); item.action(); }}
        >
          {item.label}
        </button>
      ))}
    </div>
  );
}

const menuStyles = {
  dropdown: {
    position: 'absolute',
    right: 0,
    top: 36,
    background: '#fff',
    border: '1px solid #eee',
    borderRadius: 10,
    boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
    zIndex: 999,
    minWidth: 160,
    overflow: 'hidden'
  },
  item: {
    display: 'block',
    width: '100%',
    padding: '13px 16px',
    border: 'none',
    background: 'none',
    textAlign: 'left',
    fontSize: 14,
    cursor: 'pointer',
    fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
    WebkitTapHighlightColor: 'transparent'
  }
};

export default function ClientList({ clients, onClientDeleted, onClientUpdated }) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [openMenu, setOpenMenu] = useState(null);
  const [viewingHistory, setViewingHistory] = useState(null);
  const [schedulingClient, setSchedulingClient] = useState(null);
  const [editingClient, setEditingClient] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showArchived, setShowArchived] = useState(false);
  const [archivedClients, setArchivedClients] = useState([]);
  const [loadingArchived, setLoadingArchived] = useState(false);
  const [deleting, setDeleting] = useState(null);

  // Edit modal a11y
  const editTitleId = useId();
  const editFirstFieldRef = useRef(null);
  const editTriggerRef = useRef(null);
  const searchId = useId();
  const sortId = useId();

  // Restore focus when edit modal closes
  useEffect(() => {
    if (!editingClient) return;
    editTriggerRef.current = document.activeElement;
    if (editFirstFieldRef.current && typeof editFirstFieldRef.current.focus === 'function') {
      editFirstFieldRef.current.focus();
    }
    const onKey = (e) => {
      if (e.key === 'Escape') setEditingClient(null);
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      const trigger = editTriggerRef.current;
      if (trigger && typeof trigger.focus === 'function') {
        try { trigger.focus(); } catch (e) { /* ignore */ }
      }
    };
  }, [editingClient]);

  const filtered = clients
    .filter(c =>
      c.name.toLowerCase().includes(search.toLowerCase()) ||
      c.street.toLowerCase().includes(search.toLowerCase()) ||
      c.city.toLowerCase().includes(search.toLowerCase())
    )
    .sort((a, b) => {
      if (sortBy === 'name') return a.name.localeCompare(b.name);
      if (sortBy === 'date') {
        if (!a.next_visit_date) return 1;
        if (!b.next_visit_date) return -1;
        return new Date(a.next_visit_date) - new Date(b.next_visit_date);
      }
      return 0;
    });

  const handleDelete = async (client) => {
    setOpenMenu(null);
    if (!window.confirm(t('client_list.confirm_delete', { name: client.name }))) return;
    setDeleting(client.id);
    try {
      await deleteClient(client.id);
      onClientDeleted(client.id);
    } catch {
      alert(t('client_list.err_delete'));
    }
    setDeleting(null);
  };

  const handleArchive = async (client) => {
    setOpenMenu(null);
    try {
      await archiveClient(client.id);
      onClientDeleted(client.id);
    } catch {
      alert(t('client_list.err_archive'));
    }
  };

  const handleUnarchive = async (client) => {
    setOpenMenu(null);
    try {
      await unarchiveClient(client.id);
      setArchivedClients(archivedClients.filter(c => c.id !== client.id));
    } catch {
      alert(t('client_list.err_restore'));
    }
  };

  const loadArchived = async () => {
    if (showArchived) { setShowArchived(false); return; }
    setLoadingArchived(true);
    try {
      const { data } = await getArchivedClients();
      setArchivedClients(data);
      setShowArchived(true);
    } catch {
      alert(t('client_list.err_load_archived'));
    }
    setLoadingArchived(false);
  };

  const openEdit = (client) => {
    setOpenMenu(null);
    setEditForm({
      name: client.name,
      phone: client.phone || '',
      street: client.street,
      city: client.city,
      notes: client.notes || '',
      preferred_shift: client.preferred_shift || ''
    });
    setEditingClient(client);
  };

  const handleEditSave = async () => {
    try {
      const { data } = await updateClient(editingClient.id, editForm);
      if (onClientUpdated) onClientUpdated(data);
      setEditingClient(null);
    } catch {
      alert(t('client_list.err_update'));
    }
  };

  const initials = (name) => name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
  const fmt = (n) => n > 0 ? `$${parseFloat(n).toFixed(0)}/visit` : null;

  const isDueCheck = (client) => {
    if (!client.next_visit_date) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const nextVisit = new Date(client.next_visit_date + 'T12:00:00');
    const lastVisit = client.last_visit_date
      ? new Date(client.last_visit_date + 'T12:00:00')
      : null;
    return nextVisit <= today && (!lastVisit || lastVisit < nextVisit);
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) setEditingClient(null);
  };

  const renderClient = (client, isArchived = false) => {
    const schedule = scheduleLabel(client);
    const amount = fmt(client.recurring_amount);
    const isDue = !isArchived && isDueCheck(client);
    const isMenuOpen = openMenu === client.id;

    return (
      <div key={client.id} style={{
        ...styles.card,
        ...(isDue ? styles.cardDue : {}),
        ...(isArchived ? styles.cardArchived : {})
      }}>
        <div style={styles.avatar} aria-hidden="true">{initials(client.name)}</div>
        <div style={styles.info}>
          <div style={styles.name}>{client.name}</div>
          <div style={styles.addr}>{client.street}, {client.city}</div>
          {client.phone && <div style={styles.addr}>{client.phone}</div>}
          {client.notes && <div style={styles.notes}>{client.notes}</div>}
          {schedule && (
            <div style={styles.scheduleRow}>
              <span style={styles.schedulePill}>{schedule}</span>
              {amount && <span style={styles.amountPill}>{amount}</span>}
              {isDue && <span style={styles.duePill}>{t('client_list.due')}</span>}
            </div>
          )}
        </div>

        <div style={styles.rightCol}>
          <div style={{ position: 'relative' }}>
            <button
              style={styles.menuBtn}
              onClick={e => {
                e.stopPropagation();
                setOpenMenu(isMenuOpen ? null : client.id);
              }}
              aria-label={t('a11y.client_actions_for', { name: client.name })}
              aria-haspopup="menu"
              aria-expanded={isMenuOpen}
            >
              <span aria-hidden="true">•••</span>
            </button>
            {isMenuOpen && (
              <ClientMenu
                client={client}
                isArchived={isArchived}
                t={t}
                onClose={() => setOpenMenu(null)}
                onHistory={() => { setOpenMenu(null); setViewingHistory(client); }}
                onSchedule={() => { setOpenMenu(null); setSchedulingClient(client); }}
                onEdit={() => openEdit(client)}
                onArchive={() => handleArchive(client)}
                onUnarchive={() => handleUnarchive(client)}
                onDelete={() => handleDelete(client)}
              />
            )}
          </div>
        </div>
      </div>
    );
  };

  const labelFor = (field) => {
    if (field === 'name') return t('client_list.field_name');
    if (field === 'phone') return t('client_list.field_phone');
    if (field === 'street') return t('client_list.field_street');
    if (field === 'city') return t('client_list.field_city');
    if (field === 'notes') return t('client_list.field_notes');
    return field;
  };

  return (
    <div>
      <style>{editModalCSS}</style>
      <div style={styles.topRow}>
        <label htmlFor={searchId} className="ms-sr-only">{t('a11y.search_clients')}</label>
        <input
          id={searchId}
          style={styles.search}
          placeholder={t('client_list.search_placeholder')}
          value={search}
          onChange={e => setSearch(e.target.value)}
          aria-label={t('a11y.search_clients')}
        />
        <label htmlFor={sortId} className="ms-sr-only">{t('client_list.sort_by')}</label>
        <select
          id={sortId}
          style={styles.sortSelect}
          value={sortBy}
          onChange={e => setSortBy(e.target.value)}
          aria-label={t('client_list.sort_by')}
        >
          <option value="name">{t('client_list.sort_az')}</option>
          <option value="date">{t('client_list.sort_date')}</option>
        </select>
      </div>

      <div style={styles.countRow}>
        <span style={styles.count}>
          {filtered.length === 1
            ? t('client_list.count_one', { n: filtered.length })
            : t('client_list.count_many', { n: filtered.length })}
        </span>
        <button style={styles.archivedBtn} onClick={loadArchived}>
          {loadingArchived
            ? t('client_list.loading')
            : showArchived
              ? t('client_list.hide_archived')
              : t('client_list.view_archived')}
        </button>
      </div>

      {filtered.length === 0 && <div style={styles.empty}>{t('client_list.none_found')}</div>}
      {filtered.map(client => renderClient(client, false))}

      {showArchived && (
        <>
          <div style={styles.archivedHeader}>
            {t('client_list.archived_header', { n: archivedClients.length })}
          </div>
          {archivedClients.length === 0 && <div style={styles.empty}>{t('client_list.none_archived')}</div>}
          {archivedClients.map(client => renderClient(client, true))}
        </>
      )}

      {viewingHistory && (
        <ClientHistory
          client={viewingHistory}
          onClose={() => setViewingHistory(null)}
        />
      )}

      {schedulingClient && (
        <RecurringModal
          client={schedulingClient}
          onClose={() => setSchedulingClient(null)}
          onUpdated={(updated) => {
            setSchedulingClient(null);
            if (onClientUpdated) onClientUpdated(updated);
          }}
        />
      )}

      {editingClient && (
        <div style={styles.overlay} onClick={handleOverlayClick}>
          <div
            style={styles.editModal}
            role="dialog"
            aria-modal="true"
            aria-labelledby={editTitleId}
          >
            <div style={styles.editHeader}>
              <div id={editTitleId} style={styles.editTitle}>
                {t('client_list.edit_title', { name: editingClient.name })}
              </div>
              <button
                style={styles.closeBtn}
                onClick={() => setEditingClient(null)}
                aria-label={t('a11y.close_dialog')}
              >
                <span aria-hidden="true">✕</span>
              </button>
            </div>
            {['name', 'phone', 'street', 'city', 'notes'].map((field, idx) => {
              const fieldId = `${editTitleId}-${field}`;
              const isFirst = idx === 0;
              return (
                <div key={field} style={styles.field}>
                  <label htmlFor={fieldId} style={styles.label}>
                    {labelFor(field)}
                  </label>
                  {field === 'street' ? (
                    <AddressAutocomplete
                      id={fieldId}
                      style={styles.input}
                      placeholder={t('add_client.street_placeholder')}
                      value={editForm.street || ''}
                      onStreetChange={(v) => setEditForm({ ...editForm, street: v })}
                      onSelect={({ street, city }) =>
                        setEditForm({ ...editForm, street, city: city || editForm.city })}
                      aria-label={labelFor(field)}
                    />
                  ) : field === 'phone' ? (
                    <input
                      id={fieldId}
                      ref={isFirst ? editFirstFieldRef : undefined}
                      style={styles.input}
                      inputMode="tel"
                      value={editForm.phone || ''}
                      onChange={e => setEditForm({ ...editForm, phone: formatPhone(e.target.value) })}
                      placeholder={t('add_client.phone_placeholder')}
                    />
                  ) : (
                    <input
                      id={fieldId}
                      ref={isFirst ? editFirstFieldRef : undefined}
                      style={styles.input}
                      value={editForm[field] || ''}
                      onChange={e => setEditForm({ ...editForm, [field]: e.target.value })}
                      placeholder={labelFor(field)}
                    />
                  )}
                </div>
              );
            })}
            <div style={styles.field}>
              <label htmlFor={`${editTitleId}-shift`} style={styles.label}>
                {t('client_list.field_preferred_shift')}
              </label>
              <select
                id={`${editTitleId}-shift`}
                style={styles.input}
                value={editForm.preferred_shift || ''}
                onChange={e => setEditForm({ ...editForm, preferred_shift: e.target.value })}
              >
                <option value="">{t('add_client.no_preference')}</option>
                <option value="morning">☀️ {t('add_client.shift_morning')}</option>
                <option value="afternoon">🌤️ {t('add_client.shift_afternoon')}</option>
                <option value="evening">🌙 {t('add_client.shift_evening')}</option>
              </select>
            </div>
            <div style={styles.editActions}>
              <button style={styles.cancelBtn} onClick={() => setEditingClient(null)}>
                {t('client_list.cancel')}
              </button>
              <button
                className="ms-client-save"
                style={styles.saveBtn}
                onClick={handleEditSave}
              >
                {t('client_list.save_changes')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  topRow: { display: 'flex', gap: 8, marginBottom: 8 },
  search: { flex: 1, padding: '8px 12px', border: '1px solid #ddd', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' },
  sortSelect: { padding: '8px 10px', border: '1px solid #ddd', borderRadius: 8, fontSize: 13, background: '#fff', color: '#1a1a1a' },
  countRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 },
  count: { fontSize: 12, color: '#5f6b58' },
  archivedBtn: { fontSize: 12, color: '#14653a', border: 'none', background: 'none', cursor: 'pointer' },
  empty: { textAlign: 'center', padding: 32, color: '#5f6b58', fontSize: 14 },
  card: { display: 'flex', alignItems: 'flex-start', padding: '10px 12px', border: '1px solid #eee', borderRadius: 10, marginBottom: 8, background: '#fff', gap: 10 },
  cardDue: { borderColor: '#86efac', background: '#f0fdf4' },
  cardArchived: { opacity: 0.7, background: '#fafafa' },
  avatar: { width: 36, height: 36, borderRadius: '50%', background: '#EAF3DE', color: '#14653a', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 500, flexShrink: 0, marginTop: 2 },
  info: { flex: 1, minWidth: 0 },
  name: { fontSize: 14, fontWeight: 500, color: '#1a1a1a' },
  addr: { fontSize: 12, color: '#5f6b58', marginTop: 2 },
  notes: { fontSize: 12, color: '#b45309', marginTop: 2 },
  scheduleRow: { display: 'flex', gap: 5, marginTop: 5, flexWrap: 'wrap' },
  schedulePill: { fontSize: 11, padding: '2px 8px', background: '#EAF3DE', color: '#14653a', borderRadius: 20 },
  amountPill: { fontSize: 11, padding: '2px 8px', background: '#f0f0f0', color: '#5f6b58', borderRadius: 20 },
  duePill: { fontSize: 11, padding: '2px 8px', background: '#FEF2F2', color: '#dc2626', borderRadius: 20, fontWeight: 500 },
  rightCol: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, flexShrink: 0 },
  routeBtn: { width: 32, height: 32, border: '1px solid #14653a', background: 'none', borderRadius: '50%', fontSize: 16, cursor: 'pointer', color: '#14653a', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  routeBtnActive: { background: '#EAF3DE' },
  menuBtn: { border: 'none', background: 'none', fontSize: 20, cursor: 'pointer', color: '#5f6b58', padding: '4px 6px', borderRadius: 6, letterSpacing: 2, lineHeight: 1, WebkitTapHighlightColor: 'transparent', display: 'block', minWidth: 32, minHeight: 32 },
  archivedHeader: { fontSize: 12, fontWeight: 500, color: '#5f6b58', textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: 16, marginBottom: 8, paddingBottom: 6, borderBottom: '1px solid #f0f0f0' },
  overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  editModal: { background: '#fff', borderRadius: 12, padding: 20, width: '100%', maxWidth: 400, margin: '0 16px', maxHeight: '90vh', overflowY: 'auto' },
  editHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 },
  editTitle: { fontSize: 15, fontWeight: 500, color: '#1a1a1a' },
  closeBtn: { border: 'none', background: 'none', fontSize: 16, cursor: 'pointer', color: '#5f6b58', padding: 4 },
  field: { marginBottom: 12 },
  label: { display: 'block', fontSize: 12, fontWeight: 500, color: '#5f6b58', marginBottom: 4 },
  input: { width: '100%', padding: '8px 10px', border: '1px solid #ddd', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', color: '#1a1a1a' },
  editActions: { display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 },
  cancelBtn: { padding: '8px 16px', border: '1px solid #ddd', background: 'none', borderRadius: 8, fontSize: 13, cursor: 'pointer', color: '#1a1a1a' },
  saveBtn: { padding: '8px 18px', border: 'none', background: '#14653a', borderRadius: 8, fontSize: 13, fontWeight: 500, color: '#fff', cursor: 'pointer' }
};
